import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Spinner, Chip, Tooltip, Avatar, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useTranslations } from '../../../../../contexts/translation.context';
import { Typography, UniversalTable, IdView } from '../../../../../components';
import { FilterSelect } from '../../../../../components/ui';
import {
  SearchInputIc,
  ResetFiltersIc,
  EmptyContentIc,
  InfoCircle,
} from '../../../../../components/icons';
import {
  refShareApi,
  RefShareOffer,
  OfferStatus,
  GetRefShareOffersParams,
} from '../../../../../api/services';
import { formatTimeAgo } from '../../../../../utils/formatTimeAgo';
import RevshareDetailsCell from '../../../../revshare/offers/components/RevshareDetailsCell';

interface CreatorRevShareOffersTabProps {
  creatorId: string;
}

const statusConfig: Record<OfferStatus, { bgColor: string; textColor: string; label: string }> = {
  active: { bgColor: 'rgba(23, 201, 100, 0.15)', textColor: '#17c964', label: 'Активный' },
  hidden: { bgColor: '#F4F4F5', textColor: '#71717A', label: 'Неактивный' },
  moderation: { bgColor: 'rgba(232, 163, 35, 0.15)', textColor: '#E8A323', label: 'На модерации' },
  refusal: { bgColor: 'rgba(239, 68, 68, 0.15)', textColor: '#ef4444', label: 'Отменен' },
};

interface ArpuTooltipContentProps {
  arpu7days: number;
  arpu30days: number;
}

const ArpuTooltipContent: React.FC<ArpuTooltipContentProps> = ({ arpu7days, arpu30days }) => {
  return (
    <div className="bg-white border border-[#EDEDED] rounded-[12px] shadow-[0px_0px_11px_0px_rgba(0,0,0,0.1)] p-[16px] min-w-[200px]">
      <div className="mb-[8px]">
        <Typography variant="body2" className="text-[#11181c]">
          Средний доход с пользователя
        </Typography>
      </div>
      <div className="flex flex-col gap-[4px]">
        <div className="flex items-center justify-between py-[6px]">
          <Typography variant="label3" className="text-[#888b8d]">
            7 дней
          </Typography>
          <Typography variant="label3" className="text-zinc-700 font-medium">
            ${typeof arpu7days === 'number' ? arpu7days.toFixed(2) : arpu7days}
          </Typography>
        </div>
        <div className="flex items-center justify-between py-[6px]">
          <Typography variant="label3" className="text-[#888b8d]">
            30 дней
          </Typography>
          <Typography variant="label3" className="text-zinc-700 font-medium">
            ${typeof arpu30days === 'number' ? arpu30days.toFixed(2) : arpu30days}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const CreatorRevShareOffersTab: React.FC<CreatorRevShareOffersTabProps> = ({ creatorId }) => {
  const { t } = useTranslations();

  // State
  const [offers, setOffers] = useState<RefShareOffer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [localSearch, setLocalSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('created_at');

  // Status options
  const statusOptions = useMemo(() => [
    { key: 'all', label: 'Все статусы' },
    { key: 'moderation', label: 'На модерации' },
    { key: 'active', label: 'Активный' },
    { key: 'hidden', label: 'Неактивный' },
    { key: 'refusal', label: 'Отменен' },
  ], []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchValue !== '' || statusFilter !== 'all';
  }, [searchValue, statusFilter]);

  // Define table columns
  const columns = useMemo(() => [
    { key: 'id', name: 'ID оффера' },
    { key: 'model', name: 'Модель' },
    { key: 'offerDetails', name: 'Детали RevShare' },
    { key: 'statistics', name: 'Статистика', sortable: true, sortKey: 'created_at' },
    { key: 'status', name: 'Статус' },
    { key: 'actions', name: 'Действия' },
  ], []);

  // Fetch offers for creator
  const fetchOffers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: GetRefShareOffersParams = {
        page: currentPage,
        page_size: pageSize,
        owner: creatorId,
        sort_by: sortBy as GetRefShareOffersParams['sort_by'],
      };

      if (searchValue.trim()) {
        params.search = searchValue.trim();
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter as OfferStatus;
      }

      const response = await refShareApi.getOffers(params);
      setOffers(response.objects || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Failed to load creator RevShare offers:', error);
      setOffers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [creatorId, currentPage, pageSize, searchValue, statusFilter, sortBy]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(localSearch);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Handlers
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
  };

  const handleResetFilters = () => {
    setLocalSearch('');
    setSearchValue('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  };

  // Handle status change
  const handleStatusChange = useCallback(async (modelId: string, newStatus: OfferStatus) => {
    try {
      await refShareApi.updateOfferStatus(modelId, newStatus);
      // Update local state
      setOffers((prev) =>
        prev.map((offer) => {
          const offerId = offer._id || offer.id;
          if (offerId === modelId) {
            return {
              ...offer,
              offer: offer.offer ? { ...offer.offer, status: newStatus } : { status: newStatus } as any,
            };
          }
          return offer;
        })
      );
    } catch (error) {
      console.error('Failed to update offer status:', error);
    }
  }, []);

  // Cell renderer
  const renderCell = useCallback((offer: RefShareOffer, columnKey: string) => {
    const offerId = offer._id || offer.id || '';
    const offerStatus = offer.offer?.status || 'moderation';

    switch (columnKey) {
      case 'id':
        return (
          <IdView textToCopy={offerId}>
            {offerId.slice(-4)}
          </IdView>
        );

      case 'model': {
        const modelName = offer.name || offer.of_user_name || '';
        const modelUsername = offer.of_user_name || '';
        const popularityPercent = offer.of_data?.overall_popularity_percent || 0;
        const onlyfansLink = modelUsername ? `https://onlyfans.com/${modelUsername}` : '';

        return (
          <div className="flex items-center gap-[12px]">
            <Avatar
              src={offer.avatar}
              showFallback
              name={modelName?.charAt(0)}
              className="w-[40px] h-[40px] rounded-[12px] bg-gray-200"
              classNames={{
                fallback: "bg-gray-200 text-gray-600"
              }}
            />
            <div className="flex flex-col gap-[2px]">
              {onlyfansLink ? (
                <a
                  href={onlyfansLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#006FEE] text-[14px] font-semibold hover:underline"
                >
                  {modelName}
                </a>
              ) : (
                <Typography variant="body1" className="font-semibold text-[#11181C]">
                  {modelName}
                </Typography>
              )}
              <div className="flex items-center gap-[6px]">
                {modelUsername && (
                  <Typography variant="body3" className="text-[#71717A]">
                    @{modelUsername}
                  </Typography>
                )}
                  <Chip
                    classNames={{
                      base: 'h-[18px] px-[6px] bg-[#F5A524]/20',
                      content: 'text-[#F5A524] text-[11px] font-medium'
                    }}
                  >
                    Top {popularityPercent.toFixed(1)}%
                  </Chip>
              </div>
            </div>
          </div>
        );
      }

      case 'offerDetails':
        return <RevshareDetailsCell offer={offer} />;

      case 'statistics': {
        const arpu = offer.ref_share_transactions_per_subscriber ?? 0;
        const arpu7days = (offer as any).ref_share_transactions_per_subscriber_7days ?? arpu;
        const arpu30days = (offer as any).ref_share_transactions_per_subscriber_30days ?? arpu;
        const activeCampaigns = offer.ref_share_active_total ?? 0;
        const createdAt = offer.created_at || new Date().toISOString();

        return (
          <div className="flex flex-col gap-[4px]">
            <div className="flex items-center gap-[4px]">
              <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
                ARPU:
              </Typography>
              <Typography variant="body3" className="text-[#11181c] font-semibold text-[14px] leading-[16px]">
                ${typeof arpu === 'number' ? arpu.toFixed(2) : arpu}
              </Typography>
              <Tooltip
                content={<ArpuTooltipContent arpu7days={arpu7days} arpu30days={arpu30days} />}
                placement="bottom"
                showArrow={false}
                classNames={{
                  content: 'p-0 bg-transparent shadow-lg',
                }}
              >
                <span className="cursor-pointer flex items-center">
                  <InfoCircle size={14} />
                </span>
              </Tooltip>
            </div>

            <div className="flex items-center gap-[4px]">
              <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
                Активные кампании:
              </Typography>
              <Typography variant="body3" className="text-[#11181c] font-semibold text-[14px] leading-[16px]">
                {activeCampaigns}
              </Typography>
            </div>

            <div className="flex items-center gap-[4px]">
              <Icon icon="solar:calendar-linear" className="w-[14px] h-[14px] text-zinc-500" />
              <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
                {formatTimeAgo(createdAt, t)}
              </Typography>
            </div>
          </div>
        );
      }

      case 'status': {
        const cfg = statusConfig[offerStatus] || statusConfig.moderation;
        return (
          <Chip
            className="h-[22px] px-[8px] text-[12px] font-medium"
            style={{
              backgroundColor: cfg.bgColor,
              color: cfg.textColor,
            }}
          >
            {cfg.label}
          </Chip>
        );
      }

      case 'actions': {
        const canActivate = offerStatus !== 'active' && offerStatus !== 'refusal';
        const canDeactivate = offerStatus === 'active';

        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant="light"
                isIconOnly
                className="min-w-[32px] w-[32px] h-[32px]"
              >
                <Icon icon="solar:menu-dots-bold" width={20} height={20} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Действия"
              onAction={(key: React.Key) => {
                if (key === 'activate' && canActivate) {
                  handleStatusChange(offerId, 'active');
                } else if (key === 'deactivate' && canDeactivate) {
                  handleStatusChange(offerId, 'hidden');
                }
              }}
              disabledKeys={[
                ...(canActivate ? [] : ['activate']),
                ...(canDeactivate ? [] : ['deactivate']),
              ]}
            >
              <DropdownItem key="activate" className={canActivate ? '' : 'opacity-50'}>
                <div className="flex items-center gap-[8px]">
                  <Icon icon="solar:check-circle-linear" fontSize={16} className="text-[#17c964]" />
                  <span>Активировать</span>
                </div>
              </DropdownItem>
              <DropdownItem key="deactivate" className={canDeactivate ? '' : 'opacity-50'}>
                <div className="flex items-center gap-[8px]">
                  <Icon icon="solar:close-circle-linear" fontSize={16} className="text-[#ef4444]" />
                  <span>Деактивировать</span>
                </div>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        );
      }

      default:
        return null;
    }
  }, [t, handleStatusChange]);

  // Empty content component
  const emptyContent = isLoading ? (
    <div className="flex flex-col items-center justify-center py-[48px] gap-[12px]">
      <Spinner color="primary" />
      <Typography variant="body2" className="text-[#71717A]">
        Загрузка...
      </Typography>
    </div>
  ) : (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <div className="flex items-center flex-col gap-[6px]">
          <EmptyContentIc />
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {hasActiveFilters
              ? 'По выбранным фильтрам нет офферов'
              : 'Нет офферов'
            }
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {hasActiveFilters
              ? 'Попробуйте изменить параметры фильтров'
              : 'У этого криейтора пока нет RevShare офферов'
            }
          </Typography>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col">
      {/* Filters Row */}
      <div className="flex items-center gap-[16px] flex-wrap mb-[16px]">
        {/* Search Input */}
        <Input
          type="text"
          placeholder="Поиск по ID"
          size="sm"
          value={localSearch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: "w-[273px]",
            base: "w-[273px]",
            input: "text-sm",
            inputWrapper: "rounded-lg h-[36px] w-[273px]",
          }}
        />

        {/* Status Filter */}
        <FilterSelect
          selectedKey={statusFilter}
          onSelectionChange={(value) => {
            setStatusFilter(String(value));
            setCurrentPage(1);
          }}
          options={statusOptions}
          placeholder="Статус"
          variant="campaign"
          autoWidth={true}
        />

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <Tooltip content="Сбросить фильтры" placement="top">
            <button
              className="h-[36px] w-[36px] rounded-[8px] bg-[#F4F4F5] flex items-center justify-center hover:bg-[#E4E4E7] transition-colors"
              onClick={handleResetFilters}
            >
              <ResetFiltersIc />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Table */}
      <div className="flex-1">
        <UniversalTable
          data={offers}
          columns={columns}
          renderCell={renderCell}
          emptyContent={emptyContent}
          pagination={{
            count: totalCount,
            page_size: pageSize,
            page: currentPage,
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          currentPage={currentPage}
          currentPageSize={pageSize}
          sortBy={sortBy}
          onSort={handleSort}
        />
      </div>
    </div>
  );
};

export default CreatorRevShareOffersTab;
